import * as styles from "./ProjectTable.css";

import { useAtomValue, useSetAtom } from "jotai";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { JSX } from "react";

import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@frontend/components/atoms/AlertDialog";
import { Badge } from "@frontend/components/atoms/Badge";
import { Button } from "@frontend/components/atoms/Button";
import { Icon } from "@frontend/components/atoms/Icon";
import { Input } from "@frontend/components/atoms/Input";
import { Panel } from "@frontend/components/atoms/Panel";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@frontend/components/atoms/Tooltip";
import {
  Toolbar,
  ToolbarSection,
} from "@frontend/components/molecules/Toolbar";
import {
  DataTable,
  createDataTableColumnHelper,
  useLocalDataTable,
} from "@frontend/components/organisms/DataTable";
import type { LocalDataTableColumn } from "@frontend/components/organisms/DataTable";
import { DuplicateJobDialog, JobEditorDialog } from "@frontend/features/jobs";
import { Trans, useLingui } from "@lingui/react/macro";

import {
  deleteJobAtom,
  loadJobsAtom,
  projectListsAtom,
  refreshJobsAtom,
} from "../stores/lists";
import type { JobRow, JobStatus } from "../types";

export type JobListProps = {
  projectId: number;
};

const helper = createDataTableColumnHelper<JobRow>();

const TONES: Record<JobStatus, "success" | "info" | "neutral"> = {
  current: "success",
  future: "info",
  past: "neutral",
};

const RANKS: Record<JobStatus, number> = {
  current: 0,
  future: 1,
  past: 2,
};

const NO_STATUS_RANK = 3;

export function JobList({ projectId }: JobListProps): JSX.Element {
  const { t } = useLingui();

  const { jobs, jobsLoading, error } = useAtomValue(projectListsAtom);
  const load = useSetAtom(loadJobsAtom);
  const refresh = useSetAtom(refreshJobsAtom);

  const [openJobId, setOpenJobId] = useState<number | null | undefined>();
  const [duplicating, setDuplicating] = useState<number | null>(null);
  const [removing, setRemoving] = useState<number | null>(null);

  const remove = useSetAtom(deleteJobAtom);

  // The button the copy window grows from.
  const originRef = useRef<DOMRect | null>(null);

  useEffect(() => {
    void load(projectId);
  }, [load, projectId]);

  const statusOptions = useMemo(
    () => [
      { value: "current", label: t`Current` },
      { value: "future", label: t`Future` },
      { value: "past", label: t`Past` },
    ],
    [t],
  );

  const statusLabel = useMemo(
    () =>
      Object.fromEntries(
        statusOptions.map(({ value, label }) => [value, label]),
      ) as Record<JobStatus, string>,
    [statusOptions],
  );

  const readers = useMemo(
    (): Record<string, LocalDataTableColumn<JobRow>> => ({
      workerLabel: { value: (job) => job.workerLabel },
      status: {
        value: (job) => job.status,
        searchable: false,
        sortValue: (job) =>
          null === job.status ? NO_STATUS_RANK : RANKS[job.status],
      },
      realStartDate: {
        value: (job) => job.realStartDate ?? job.startDate,
        searchable: false,
      },
      realStopDate: {
        value: (job) => job.realStopDate ?? job.stopDate,
        searchable: false,
      },
      taskCount: { value: (job) => job.taskCount },
    }),
    [],
  );

  const [hidePast, setHidePast] = useState(true);
  const [savedJobId, setSavedJobId] = useState<number | null>(null);

  const predicate = useCallback(
    (job: JobRow) =>
      !hidePast || "past" !== job.status || job.id === savedJobId,
    [hidePast, savedJobId],
  );

  const local = useLocalDataTable({
    rows: jobs,
    columns: readers,
    initialSorting: [{ id: "status", desc: false }],
    predicate,
  });

  const columns = useMemo(
    () =>
      helper.columns([
        helper.accessor("workerLabel", {
          header: t`Worker`,
          meta: { minWidth: "220px", filter: "text", truncate: true },
          cell: ({ row, getValue }) => (
            <span className={styles.name}>
              {getValue() ?? <span className={styles.muted}>{t`Nobody`}</span>}

              {row.original.replacement ? (
                <Badge variant="warning" size="sm">
                  <Trans>Replacement</Trans>
                </Badge>
              ) : null}
            </span>
          ),
        }),
        helper.accessor("status", {
          header: t`Status`,
          meta: {
            width: "120px",
            filter: { type: "select", options: statusOptions },
          },
          cell: ({ getValue }) => {
            const status = getValue();

            return null === status ? null : (
              <Badge variant={TONES[status]} size="sm">
                {statusLabel[status]}
              </Badge>
            );
          },
        }),
        helper.accessor("realStartDate", {
          header: t`From`,
          meta: { minWidth: "110px", maxWidth: "130px" },
          cell: ({ row, getValue }) => getValue() ?? row.original.startDate,
        }),
        helper.accessor("realStopDate", {
          header: t`To`,
          meta: { minWidth: "110px", maxWidth: "130px" },
          cell: ({ row, getValue }) =>
            getValue() ??
            row.original.stopDate ?? (
              <span className={styles.muted}>{t`Open`}</span>
            ),
        }),
        helper.accessor("taskCount", {
          header: t`Tasks`,
          // The header carries a sort and a filter beside its own word.
          meta: { minWidth: "110px", maxWidth: "130px", filter: "number" },
        }),
        helper.display({
          id: "actions",
          header: t`Action`,
          enableSorting: false,
          meta: { width: "110px" },
          cell: ({ row }) => (
            <span
              className={styles.rowActions}
              // The row itself opens the post, and these do something else.
              onClick={(event) => event.stopPropagation()}
            >
              {/* A replacement has nothing of its own to replace, so the
                  legacy list offers no copy on one either. */}
              {row.original.replacement ? null : (
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <Button
                        variant="ghost"
                        size="sm"
                        aria-label={t`Duplicate the work post`}
                        onClick={(event) => {
                          originRef.current =
                            event.currentTarget.getBoundingClientRect();
                          setDuplicating(row.original.id);
                        }}
                      >
                        <Icon name="Copy" size="sm" />
                      </Button>
                    }
                  />

                  <TooltipContent side="left">
                    {t`Duplicate the work post`}
                  </TooltipContent>
                </Tooltip>
              )}

              <Tooltip>
                <TooltipTrigger
                  render={
                    <Button
                      variant="ghost"
                      size="sm"
                      aria-label={t`Delete the work post`}
                      onClick={(event) => {
                        originRef.current =
                          event.currentTarget.getBoundingClientRect();
                        setRemoving(row.original.id);
                      }}
                    >
                      <Icon name="Trash2" size="sm" />
                    </Button>
                  }
                />

                <TooltipContent side="left">
                  {t`Delete the work post`}
                </TooltipContent>
              </Tooltip>
            </span>
          ),
        }),
      ]),
    [t, statusOptions, statusLabel],
  );

  return (
    <div className={styles.table}>
      {error ? (
        <p className={styles.alert} role="alert">
          {error}
        </p>
      ) : null}

      <Panel overflow="visible">
        <Toolbar wrap={false}>
          <ToolbarSection>
            <Button
              variant="link"
              size="sm"
              leftSection={<Icon name="Plus" size="sm" />}
              onClick={(event) => {
                originRef.current = event.currentTarget.getBoundingClientRect();
                setOpenJobId(null);
              }}
            >
              {t`Add a work post`}
            </Button>

            <Button
              variant="ghost"
              size="sm"
              leftSection={
                <Icon name={hidePast ? "Eye" : "EyeOff"} size="sm" />
              }
              onClick={() => setHidePast((hidden) => !hidden)}
            >
              {hidePast
                ? t`Show the past work posts`
                : t`Hide the past work posts`}
            </Button>
          </ToolbarSection>

          <ToolbarSection className={styles.search}>
            <Input
              value={local.search}
              placeholder={t`Search a worker`}
              leftSection={<Icon name="Search" size="sm" />}
              onValueChange={local.setSearch}
            />
          </ToolbarSection>
        </Toolbar>

        <DataTable
          columns={columns}
          data={local.rows}
          getRowId={(job) => String(job.id)}
          loading={jobsLoading}
          loadingRowCount={4}
          emptyMessage={
            "" === local.search && 0 === local.columnFilters.length ? (
              <Trans>This project has no work post yet.</Trans>
            ) : (
              <Trans>No work post matches this search.</Trans>
            )
          }
          size="sm"
          variant="ghost"
          horizontalSpacing="sm"
          verticalSpacing="sm"
          bordered={false}
          sorting={local.sorting}
          onSortingChange={local.onSortingChange}
          columnFilters={local.columnFilters}
          onColumnFiltersChange={local.onColumnFiltersChange}
          onRowClick={(job, event) => {
            originRef.current = event.currentTarget.getBoundingClientRect();
            setOpenJobId(job.id);
          }}
        />
      </Panel>

      <AlertDialog
        open={null !== removing}
        originRef={originRef}
        onOpenChange={(open) => {
          if (!open) {
            setRemoving(null);
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              <Trans>Delete this work post?</Trans>
            </AlertDialogTitle>

            <AlertDialogDescription>
              <Trans>
                The post and its tasks are removed for good. One whose tasks
                carry done or validated hours is refused, and the table says
                why.
              </Trans>
            </AlertDialogDescription>
          </AlertDialogHeader>

          <AlertDialogFooter>
            <AlertDialogCancel>
              <Trans>Cancel</Trans>
            </AlertDialogCancel>

            <AlertDialogAction
              variant="destructive"
              onClick={() => {
                if (null !== removing) {
                  void remove(removing);
                }

                setRemoving(null);
              }}
            >
              <Trans>Delete</Trans>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {null === duplicating ? null : (
        <DuplicateJobDialog
          open
          onOpenChange={(next) => {
            if (!next) {
              setDuplicating(null);
            }
          }}
          projectId={projectId}
          jobId={duplicating}
          onDuplicated={() => {
            setDuplicating(null);
            void refresh();
          }}
          originRef={originRef}
        />
      )}

      {/* Keyed on the record: the editor loads on mount, and two rows are two
          different jobs. */}
      {undefined === openJobId ? null : (
        <JobEditorDialog
          key={openJobId ?? "new"}
          open
          onOpenChange={(next) => {
            if (!next) {
              setOpenJobId(undefined);
            }
          }}
          projectId={projectId}
          jobId={openJobId}
          onSaved={(savedId) => {
            setOpenJobId(undefined);
            setSavedJobId(savedId);
            void refresh();
          }}
          onCancel={() => setOpenJobId(undefined)}
          originRef={originRef}
        />
      )}
    </div>
  );
}
